import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, getDoc, updateDoc, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import AISummary from '../../components/AISummary';
import CoachPerformance from '../../components/CoachPerformance';
import LearnerMetrics from '../../components/LearnerMetrics';
import UserManager from '../../components/UserManager';
import RubricEditor from '../../components/RubricEditor';
import ProgrammeManager from '../../components/ProgrammeManager';
import ReportRenderer from '../../components/ReportRenderer';
import { SessionCard } from '../coach/CoachCompanyPage';
import CanvasCoachView from '../../components/CanvasCoachView';

const STAGE_COLORS = {
  'Problem-Solution Fit':        '#1d4ed8',
  'Product-Market Fit':          '#0369a1',
  'Early Growth & Monetization': '#166534',
  'Scaling & Regional Expansion':'#065f46',
};

const STAGES = [
  'Problem-Solution Fit', 'Product-Market Fit',
  'Early Growth & Monetization', 'Scaling & Regional Expansion',
];

const COHORT_ID = 'cohort_rfc_ethiopia_edtech_2026';

export default function TeamDashboard() {
  const { profile } = useAuth();
  const [allCohorts, setAllCohorts]   = useState([]);
  const [allClients, setAllClients]   = useState([]);
  const [allPrograms, setAllPrograms] = useState([]);
  const [filterClientId, setFilterClientId]   = useState('all');
  const [filterProgramId, setFilterProgramId] = useState('all');
  const [filterCohortId, setFilterCohortId]   = useState('all');
  const selectedCohort = filterCohortId !== 'all' ? allCohorts.find(c => c.id === filterCohortId) : null;
  const [view, setView] = useState(() => {
    const hash = window.location.hash.replace('#','');
    return ['overview','companies','coaches','config','programmes','reports'].includes(hash) ? hash : 'overview';
  });
  const [companies, setCompanies] = useState([]);
  const [coaches, setCoaches]     = useState([]);
  const [cohort, setCohort]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);

  useEffect(() => {
    const onHash = () => {
      const hash = window.location.hash.replace('#','');
      if (['overview','companies','coaches','config','programmes','reports'].includes(hash)) {
        setView(hash); setSelectedCompany(null);
      }
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    async function load() {
      try {
        const [cSnap, coachSnap, cohortDoc, clientSnap, programSnap, cohortSnap] = await Promise.all([
          getDocs(collection(db, 'companies')),
          getDocs(query(collection(db, 'users'), where('role', '==', 'coach'))),
          getDoc(doc(db, 'cohorts', COHORT_ID)),
          getDocs(collection(db, 'clients')),
          getDocs(collection(db, 'programs')),
          getDocs(collection(db, 'cohorts')),
        ]);
        setCompanies(cSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setCoaches(coachSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setCohort(cohortDoc.data());
        setAllClients(clientSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setAllPrograms(programSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setAllCohorts(cohortSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    }
    load();
  }, []);

  const setHash = (h) => { window.location.hash = h; setView(h); setSelectedCompany(null); };

  // Live-update a single company in local state without a full reload
  const handleCompanyUpdated = (companyId, updates) => {
    setCompanies(prev => prev.map(c => c.id === companyId ? { ...c, ...updates } : c));
  };

  const headerSubtitle = (() => {
    if (filterCohortId !== 'all') {
      const c = allCohorts.find(c => c.id === filterCohortId);
      return c?.name || 'Scaling Readiness Platform';
    }
    if (filterProgramId !== 'all') {
      const p = allPrograms.find(p => p.id === filterProgramId);
      return p?.name || 'Scaling Readiness Platform';
    }
    if (filterClientId !== 'all') {
      const cl = allClients.find(c => c.id === filterClientId);
      return cl ? `${cl.name} — all programmes` : 'Scaling Readiness Platform';
    }
    return 'All clients — Scaling Readiness Platform';
  })();

  const filteredCompanies = companies.filter(c => {
    if (filterCohortId !== 'all') return c.cohortId === filterCohortId;
    if (filterProgramId !== 'all') return c.programId === filterProgramId;
    if (filterClientId !== 'all') {
      const prog = allPrograms.find(p => p.id === c.programId);
      return prog?.clientId === filterClientId;
    }
    return true;
  });

  // Coaches whose actual companies fall within the current filter — used for
  // performance display (Coaches tab) where we only want coaches truly active in scope.
  const companyCoachIds = new Set(filteredCompanies.map(c => c.coachId).filter(Boolean));
  const filteredCoaches = (filterClientId === 'all' && filterProgramId === 'all' && filterCohortId === 'all')
    ? coaches
    : coaches.filter(co => companyCoachIds.has(co.uid || co.id));

  // Coaches assignable to companies in the current filter — broader than filteredCoaches,
  // since a coach should be assignable to a company in a cohort even before they have any
  // companies there yet. Scoped by program membership (cohort is too granular for a coach's
  // own programIds field), then narrowed to the program that owns the filtered cohort if set.
  const assignmentProgramIds = new Set(
    filterCohortId !== 'all'
      ? [allCohorts.find(c => c.id === filterCohortId)?.programId].filter(Boolean)
      : filterProgramId !== 'all'
        ? [filterProgramId]
        : filterClientId !== 'all'
          ? allPrograms.filter(p => p.clientId === filterClientId).map(p => p.id)
          : allPrograms.map(p => p.id)
  );
  const assignableCoaches = (filterClientId === 'all' && filterProgramId === 'all' && filterCohortId === 'all')
    ? coaches
    : coaches.filter(co => {
        if (co.programIds?.some(pid => assignmentProgramIds.has(pid))) return true;
        return companyCoachIds.has(co.uid || co.id); // fallback for legacy data missing programIds
      });

  const stageDistribution = STAGES.map(stage => ({
    stage, count: filteredCompanies.filter(c => c.currentStage === stage).length,
    color: STAGE_COLORS[stage],
  })).filter(s => s.count > 0);

  return (
    <DashboardLayout>
      <div className="gm-page">
        {/* Header */}
        <div className="gm-page-header">
          <div>
            <p className="gm-page-welcome">Mirepa Team</p>
            <h1 className="gm-page-title">Programme Administration</h1>
            <p className="gm-page-sub">{headerSubtitle}</p>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:8, alignItems:'flex-end' }}>
            {view !== 'programmes' && <TeamProgrammeSelector
              clients={allClients} programs={allPrograms} cohorts={allCohorts}
              filterClientId={filterClientId} setFilterClientId={(v) => { setFilterClientId(v); setFilterProgramId('all'); setFilterCohortId('all'); }}
              filterProgramId={filterProgramId} setFilterProgramId={(v) => { setFilterProgramId(v); setFilterCohortId('all'); }}
              filterCohortId={filterCohortId} setFilterCohortId={setFilterCohortId}
            />}
            <div className="gm-client-tabs">
            {['overview','companies','coaches','config','programmes','reports'].map(v => (
              <button key={v} className={`gm-client-tab ${view === v || (v === 'companies' && selectedCompany) ? 'gm-client-tab--active' : ''}`}
                onClick={() => setHash(v)}>
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
          </div>
        </div>

        {loading && <div className="gm-loading"><div className="gm-spinner" /></div>}

        {/* ── Overview ── */}
        {!loading && view === 'overview' && !selectedCompany && (
          <OverviewTab
            companies={filteredCompanies}
            stageDistribution={stageDistribution} cohort={cohort}
            selectedCohort={selectedCohort}
            filterCohortId={filterCohortId}
            filterProgramId={filterProgramId}
            filterClientId={filterClientId}
            allCohorts={allCohorts}
            allPrograms={allPrograms}
          />
        )}

        {/* ── Companies ── */}
        {!loading && (view === 'companies') && !selectedCompany && (
          <CompaniesTab
            companies={filteredCompanies}
            coaches={filteredCoaches} onSelect={c => setSelectedCompany(c)} />
        )}

        {/* ── Company detail ── */}
        {!loading && selectedCompany && (
          <TeamCompanyDetail
            company={selectedCompany}
            onBack={() => setSelectedCompany(null)}
            profile={profile}
          />
        )}

        {/* ── Coaches ── */}
        {!loading && view === 'coaches' && (
          <CoachesTab coaches={filteredCoaches} />
        )}

        {/* ── Config ── */}
        {!loading && view === 'config' && (
          <ConfigTab cohort={cohort} setCohort={setCohort} companies={filteredCompanies}
            coaches={filteredCoaches} assignableCoaches={assignableCoaches}
            selectedCohort={selectedCohort}
            filterCohortId={filterCohortId} filterProgramId={filterProgramId} filterClientId={filterClientId}
            allCohorts={allCohorts} allPrograms={allPrograms} allClients={allClients}
            onCompanyUpdated={handleCompanyUpdated} />
        )}

        {/* ── Programmes ── */}
        {!loading && view === 'programmes' && (
          <div>
            <ProgrammeManager />
          </div>
        )}

        {/* ── Reports ── */}
        {!loading && view === 'reports' && (
          <ReportsTab companies={filteredCompanies} coaches={filteredCoaches} cohort={cohort} headerSubtitle={headerSubtitle}
            filterProgramId={filterProgramId} filterCohortId={filterCohortId} filterClientId={filterClientId} />
        )}
      </div>
    </DashboardLayout>
  );
}

// ─── Overview tab ─────────────────────────────────────────────
function OverviewTab({ companies, stageDistribution, cohort, selectedCohort, filterCohortId, filterProgramId, filterClientId, allCohorts, allPrograms }) {
  const advancedCompanies = companies.filter(c => c.latestStage && c.latestStage !== c.currentStage).length;

  // Determine which cohorts are relevant to current filter, and only include ones with a learner target set
  const relevantCohorts = allCohorts.filter(co => {
    const hasTarget = co.targets?.primaryMetric?.avgPerCompany;
    if (!hasTarget) return false;
    if (filterCohortId !== 'all') return co.id === filterCohortId;
    if (filterProgramId !== 'all') return co.programId === filterProgramId;
    if (filterClientId !== 'all') {
      const prog = allPrograms.find(p => p.id === co.programId);
      return prog?.clientId === filterClientId;
    }
    return true;
  });

  return (
    <>
      <div className="gm-kpi-row">
        <div className="gm-kpi-card">
          <div className="gm-kpi-value">{companies.length}</div>
          <div className="gm-kpi-label">Companies</div>
        </div>
        <div className="gm-kpi-card">
          <div className="gm-kpi-value">{[...new Set(companies.map(c => c.coachId).filter(Boolean))].length}</div>
          <div className="gm-kpi-label">Coaches</div>
        </div>
        <div className="gm-kpi-card gm-kpi-card--accent">
          <div className="gm-kpi-value">{advancedCompanies}</div>
          <div className="gm-kpi-label">Companies advanced a stage</div>
        </div>
        <div className="gm-kpi-card">
          <div className="gm-kpi-value">{cohort?.currentPeriod || '—'}</div>
          <div className="gm-kpi-label">Current period</div>
        </div>
      </div>

      <div className="gm-section">
        <h2 className="gm-section-title">Stage distribution</h2>
        <div className="gm-stage-bars">
          {stageDistribution.map(({ stage, count, color }) => (
            <div key={stage} className="gm-stage-bar-row">
              <div className="gm-stage-bar-label">{stage}</div>
              <div className="gm-stage-bar-track">
                <div className="gm-stage-bar-fill" style={{ width:`${(count/companies.length)*100}%`, background:color }} />
              </div>
              <div className="gm-stage-bar-count">{count}</div>
            </div>
          ))}
        </div>
      </div>

      {relevantCohorts.length > 0 && (() => {
        // Group cohorts by their metric label — different metrics (e.g. "Active learners"
        // vs "Smallholder farmers impacted") must never be aggregated into one panel.
        const groups = {};
        relevantCohorts.forEach(co => {
          const label = co.targets?.primaryMetric?.label || 'Primary metric';
          if (!groups[label]) groups[label] = [];
          groups[label].push(co.id);
        });
        return Object.entries(groups).map(([label, ids]) => (
          <div className="gm-section" key={label}>
            <LearnerMetrics
              cohortId={filterCohortId !== 'all' ? filterCohortId : undefined}
              cohortIds={filterCohortId === 'all' ? ids : undefined}
              isEditable={filterCohortId !== 'all'}
            />
          </div>
        ));
      })()}
    </>
  );
}

// ─── Companies tab ────────────────────────────────────────────
function CompaniesTab({ companies, coaches, onSelect }) {
  const [search, setSearch] = useState('');
  const filtered = companies.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase())
  ).sort((a,b) => STAGES.indexOf(b.currentStage) - STAGES.indexOf(a.currentStage));

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <input
          type="text" placeholder="Search companies…"
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ padding:'10px 14px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:'0.875rem', width:280, fontFamily:'inherit', outline:'none' }}
        />
      </div>
      <div className="gm-table-wrap">
        <table className="gm-table">
          <thead>
            <tr>
              <th>Company</th>
              <th>Current stage</th>
              <th>Progress</th>
              <th>Target</th>
              <th>Coach</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => {
              const coach = coaches.find(co => co.uid === c.coachId || co.id === c.coachId);
              const color = STAGE_COLORS[c.currentStage] || '#4a5481';
              return (
                <tr key={c.id}>
                  <td style={{ fontWeight:600 }}>{c.name}</td>
                  <td><span style={{ color, fontWeight:600, fontSize:'0.82rem' }}>{c.currentStage}</span></td>
                  <td>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <div className="gm-mini-progress" style={{ width:80 }}>
                        <div className="gm-mini-progress-fill" style={{ width:`${c.currentStageProgress||0}%`, background:color }} />
                      </div>
                      <span style={{ fontSize:'0.72rem', color:'#6b6b6b' }}>{c.currentStageUpgrade}</span>
                    </div>
                  </td>
                  <td style={{ fontSize:'0.82rem', color:STAGE_COLORS[c.targetStage] }}>{c.targetStage}</td>
                  <td style={{ fontSize:'0.82rem' }}>{coach?.displayName || '—'}</td>
                  <td>
                    <button className="gm-btn gm-btn-sm gm-btn-outline" onClick={() => onSelect(c)}>
                      View →
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── Team company detail ──────────────────────────────────────
function TeamCompanyDetail({ company, onBack, profile }) {
  const [sessions, setSessions]   = useState([]);
  const [expanded, setExpanded]   = useState(null);
  const [activeTab, setActiveTab] = useState('summary');
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const sSnap = await getDocs(
          query(collection(db, 'companies', company.id, 'coachingSessions'),
            { orderBy: 'sessionDate' })
        );
        setSessions(sSnap.docs.map(d => ({ id: d.id, ...d.data() }))
          .sort((a,b) => (b.sessionDate?.seconds||0) - (a.sessionDate?.seconds||0)));
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    }
    load();
  }, [company.id]);

  const color = STAGE_COLORS[company.currentStage] || '#4a5481';

  return (
    <div>
      <button className="gm-back-btn" onClick={onBack}>← All companies</button>
      <div style={{ marginTop:16, marginBottom:24 }}>
        <h2 className="gm-page-title">{company.name}</h2>
        <p className="gm-page-sub">{company.description}</p>
      </div>

      {/* Stage comparison */}
      <div className="gm-stage-compare" style={{ marginBottom:24 }}>
        <div className="gm-stage-compare-card">
          <div className="gm-stage-compare-label">Baseline</div>
          <div className="gm-stage-compare-stage" style={{ color }}>{company.currentStage}</div>
          <div className="gm-stage-compare-upgrade">{company.currentStageUpgrade}</div>
          <div className="gm-mini-progress" style={{ marginTop:8 }}>
            <div className="gm-mini-progress-fill" style={{ width:`${company.currentStageProgress||0}%`, background:color }} />
          </div>
          <div className="gm-mini-progress-label">{company.currentStageProgress}% through stage</div>
        </div>
        {company.latestStage && (
          <div className="gm-stage-compare-card gm-stage-compare-card--current">
            <div className="gm-stage-compare-label">Latest assessment</div>
            <div className="gm-stage-compare-stage" style={{ color:STAGE_COLORS[company.latestStage] }}>{company.latestStage}</div>
            <div className="gm-stage-compare-upgrade">{company.latestStageUpgrade}</div>
            <div className="gm-mini-progress" style={{ marginTop:8 }}>
              <div className="gm-mini-progress-fill" style={{ width:`${company.latestStageProgress||0}%`, background:STAGE_COLORS[company.latestStage] }} />
            </div>
            <div className="gm-mini-progress-label">{company.latestStageProgress}% through stage</div>
          </div>
        )}
        <div className="gm-stage-compare-card gm-stage-compare-card--target">
          <div className="gm-stage-compare-label">Target (Dec 2026)</div>
          <div className="gm-stage-compare-stage" style={{ color:STAGE_COLORS[company.targetStage] }}>{company.targetStage}</div>
          <div className="gm-stage-compare-upgrade">{company.targetStageUpgrade}</div>
          <div className="gm-mini-progress" style={{ marginTop:8 }}>
            <div className="gm-mini-progress-fill" style={{ width:`${company.targetStageProgress||0}%`, background:STAGE_COLORS[company.targetStage] }} />
          </div>
          <div className="gm-mini-progress-label">{company.targetStageProgress}% through stage</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="gm-client-tabs" style={{ marginBottom:24 }}>
        {['summary','sessions','canvas'].map(tab => (
          <button key={tab} className={`gm-client-tab ${activeTab===tab?'gm-client-tab--active':''}`}
            onClick={() => setActiveTab(tab)}>
            {tab==='summary'?'AI Summary':tab==='sessions'?'Sessions':'Canvas & Journey'}
          </button>
        ))}
      </div>

      {activeTab === 'summary' && (
        <AISummary companyId={company.id} companyName={company.name} role="team" />
      )}

      {activeTab === 'sessions' && (
        <div>
          {loading && <div className="gm-loading"><div className="gm-spinner" /></div>}
          {!loading && sessions.length === 0 && <div className="gm-empty-state"><p>No sessions yet.</p></div>}
          {!loading && sessions.map(s => (
            <SessionCard key={s.id} session={s} companyId={company.id}
              isExpanded={expanded===s.id}
              onToggle={() => setExpanded(expanded===s.id?null:s.id)}
              onToggleAction={() => {}} isCoach={false} profile={profile} readOnly={true} />
          ))}
        </div>
      )}

      {activeTab === 'canvas' && (
        <CanvasCoachView companyId={company.id} profile={profile} />
      )}
    </div>
  );
}

// ─── Coaches tab ──────────────────────────────────────────────
function CoachesTab({ coaches }) {
  return (
    <>
      <h2 className="gm-section-title">Coach performance</h2>
      <p style={{ fontSize:'0.82rem', color:'#6b6b6b', marginBottom:24 }}>
        Click any coach to expand their performance metrics and AI summary.
      </p>
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {coaches.map(c => (
          <CoachPerformance key={c.uid||c.id} coachId={c.uid||c.id} coachName={c.displayName} showAI={true} />
        ))}
      </div>
    </>
  );
}

// ─── Config tab ───────────────────────────────────────────────
function ConfigTab({ cohort, setCohort, companies, coaches, assignableCoaches, selectedCohort, filterCohortId, filterProgramId, filterClientId, allCohorts, allPrograms, allClients, onCompanyUpdated }) {
  const [coachAssignments, setCoachAssignments] = useState({});
  const [savingCoach, setSavingCoach]   = useState(null);

  useEffect(() => {
    const assignments = {};
    companies.forEach(c => { assignments[c.id] = c.coachId || ''; });
    setCoachAssignments(assignments);
  }, [companies]);

  const saveCoachAssignment = async (companyId, coachId) => {
    setSavingCoach(companyId);
    try {
      const coachObj = coaches.find(co => (co.uid || co.id) === coachId);
      await updateDoc(doc(db, 'companies', companyId), {
        coachId: coachId || '',
        coachName: coachObj?.displayName || '',
        updatedAt: new Date(),
      });
      // Reflect immediately in parent state without a full reload
      if (onCompanyUpdated) {
        onCompanyUpdated(companyId, { coachId: coachId || '', coachName: coachObj?.displayName || '' });
      }
    } catch (err) { console.error(err); }
    finally { setSavingCoach(null); }
  };

  return (
    <>
      {/* Coach assignments */}
      <div className="gm-section">
        <h2 className="gm-section-title">Coach assignments</h2>
        <div className="gm-table-wrap">
          <table className="gm-table">
            <thead>
              <tr><th>Company</th><th>Current stage</th><th>Assigned coach</th><th></th></tr>
            </thead>
            <tbody>
              {companies.map(c => (
                <tr key={c.id}>
                  <td style={{ fontWeight:600 }}>{c.name}</td>
                  <td><span style={{ color:STAGE_COLORS[c.currentStage], fontSize:'0.78rem', fontWeight:600 }}>{c.currentStage}</span></td>
                  <td>
                    <select
                      value={coachAssignments[c.id] || ''}
                      onChange={e => setCoachAssignments(prev => ({ ...prev, [c.id]: e.target.value }))}
                      style={{ padding:'6px 10px', border:'1.5px solid #e2e8f0', borderRadius:6, fontSize:'0.82rem', fontFamily:'inherit' }}
                    >
                      <option value="">— Unassigned —</option>
                      {assignableCoaches.map(co => (
                        <option key={co.uid||co.id} value={co.uid||co.id}>{co.displayName}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button
                      className="gm-btn gm-btn-sm gm-btn-outline"
                      onClick={() => saveCoachAssignment(c.id, coachAssignments[c.id])}
                      disabled={savingCoach === c.id}
                    >
                      {savingCoach === c.id ? 'Saving…' : 'Save'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Learner targets — only show for cohorts with a learner target configured */}
      {(() => {
        const relevantCohorts = allCohorts.filter(co => {
          const hasTarget = co.targets?.primaryMetric?.avgPerCompany;
          if (!hasTarget) return false;
          if (filterCohortId !== 'all') return co.id === filterCohortId;
          if (filterProgramId !== 'all') return co.programId === filterProgramId;
          if (filterClientId !== 'all') {
            const prog = allPrograms.find(p => p.id === co.programId);
            return prog?.clientId === filterClientId;
          }
          return true;
        });
        if (relevantCohorts.length === 0) return null;
        const groups = {};
        relevantCohorts.forEach(co => {
          const label = co.targets?.primaryMetric?.label || 'Primary metric';
          if (!groups[label]) groups[label] = [];
          groups[label].push(co.id);
        });
        return (
          <div className="gm-section">
            <h2 className="gm-section-title">Programme targets</h2>
            {Object.entries(groups).map(([label, ids]) => (
              <div key={label} style={{ marginBottom: 16 }}>
                <LearnerMetrics
                  cohortId={filterCohortId !== 'all' ? filterCohortId : undefined}
                  cohortIds={filterCohortId === 'all' ? ids : undefined}
                  isEditable={filterCohortId !== 'all'}
                />
              </div>
            ))}
          </div>
        );
      })()}

      {/* User management */}
      <div className="gm-section">
        <h2 className="gm-section-title">User management</h2>
        <UserManager
          programId={filterProgramId !== 'all' ? filterProgramId : null}
          scopedCompanies={companies}
          allCompanyCount={companies.length}
          allPrograms={allPrograms}
          allClients={allClients}
        />
      </div>

      {/* Rubric editor */}
      <div className="gm-section">
        <h2 className="gm-section-title">Assessment rubric</h2>
        <RubricEditor cohortId={selectedCohort?.id || null} allCohorts={allCohorts} />
      </div>
    </>
  );
}

// ─── Reports tab ──────────────────────────────────────────────
function ReportsTab({ companies, coaches, cohort, headerSubtitle, filterProgramId, filterCohortId, filterClientId }) {
  const [generating, setGenerating] = useState(false);
  const [report, setReport]         = useState(null);
  const [editing, setEditing]       = useState(false);
  const [editDraft, setEditDraft]   = useState('');
  const [saving, setSaving]         = useState(false);
  const [savedReport, setSavedReport] = useState(null); // final saved version

  const generateReport = async () => {
    setGenerating(true);
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions(undefined, 'europe-west1');
      const fn = httpsCallable(functions, 'generateAISummary');

      const stageDistribution = STAGES.map(stage => ({
        stage, count: companies.filter(c => c.currentStage === stage).length
      }));

      const scopeLabel = headerSubtitle || 'the selected programme';

      const prompt =
        'You are a programme analyst at an enterprise support organisation. Generate a comprehensive programme report for: ' + scopeLabel + '.\n\n' +
        'PROGRAMME OVERVIEW:\n' +
        'Total companies: ' + companies.length + '\n' +
        'Current period: ' + (cohort?.currentPeriod || 'current period') + '\n' +
        'Coaches: ' + coaches.length + '\n\n' +
        'STAGE DISTRIBUTION:\n' +
        stageDistribution.map(s => s.stage + ': ' + s.count + ' companies').join('\n') + '\n\n' +
        'COMPANY PROGRESS:\n' +
        companies.map(c =>
          c.name + ' — ' + c.currentStage + ' (' + c.currentStageUpgrade + ', ' + c.currentStageProgress + '% through stage) → Target: ' + c.targetStage
        ).join('\n') + '\n\n' +
        'Write a structured programme report covering:\n' +
        '1. Executive summary (2-3 sentences)\n' +
        '2. Overall programme progress and stage distribution\n' +
        '3. Highlights — companies making strong progress\n' +
        '4. Areas of concern — companies needing acceleration\n' +
        '5. Coaching cadence and quality observations\n' +
        '6. Recommendations for the next period\n\n' +
        'Write professionally, suitable for sharing with the relevant client. Use clear headings. Be specific and data-driven. Do not mention any client or programme name other than what is specified above.';

      const result = await fn({ prompt, companyId: 'programme_report' });
      const generatedText = result.data?.summary || 'Report generation failed.';
      setReport(generatedText);
      setEditDraft(generatedText);
      setEditing(false);
    } catch (err) { console.error(err); setReport('Failed to generate report. Please try again.'); }
    finally { setGenerating(false); }
  };

  const printReport = () => window.print();

  return (
    <>
      <div className="gm-section gm-report-generate-panel">
        <h2 className="gm-section-title">Programme reports</h2>
        <div className="gm-config-card">
          <div className="gm-config-label">Generate period report</div>
          <p className="gm-config-desc">
            AI-generated narrative report covering programme progress, company highlights, coaching performance and recommendations.
            Can be printed or saved as PDF using your browser's print function.
          </p>
          <div style={{ display:'flex', gap:12, marginTop:12, flexWrap:'wrap' }}>
            <button className="gm-btn gm-btn-primary" onClick={generateReport} disabled={generating}>
              {generating ? 'Generating…' : report ? '↻ Regenerate' : '✦ Generate programme report'}
            </button>
            {report && !editing && (
              <button className="gm-btn gm-btn-outline" onClick={() => { setEditing(true); setEditDraft(savedReport || report); }}>
                ✏️ Edit report
              </button>
            )}
            {(savedReport || report) && !editing && (
              <button className="gm-btn gm-btn-outline" onClick={printReport}>
                🖨 Print / Save as PDF
              </button>
            )}
          </div>
        </div>
      </div>

      {report && (
        <div className="gm-section gm-report-output" id="programme-report">
          <div className="gm-report-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
            <div>
              <h2>{headerSubtitle || 'Scaling Readiness Programme'}</h2>
              <p>Period: {cohort?.currentPeriod || 'current'} · {savedReport ? 'Final version saved' : 'AI draft — not yet saved'}</p>
            </div>
            {savedReport && !editing && (
              <span className="gm-validated-badge">✓ Final version saved</span>
            )}
          </div>

          {/* Edit mode */}
          {editing ? (
            <div style={{ marginTop:16 }}>
              <p style={{ fontSize:'0.78rem', color:'#6b6b6b', marginBottom:8 }}>
                Edit the report below. You can add context, remove sections, or adjust the AI's language before saving the final version.
              </p>
              <textarea
                value={editDraft}
                onChange={e => setEditDraft(e.target.value)}
                style={{ width:'100%', minHeight:480, padding:'16px', border:'1.5px solid #0369a1', borderRadius:8, fontSize:'0.875rem', fontFamily:'inherit', lineHeight:1.7, outline:'none', resize:'vertical', boxSizing:'border-box' }}
              />
              <div style={{ display:'flex', gap:12, marginTop:12 }}>
                <button className="gm-btn gm-btn-primary" disabled={saving}
                  onClick={async () => {
                    setSaving(true);
                    try {
                      const { addDoc, collection: col, serverTimestamp: sts } = await import('firebase/firestore');
                      await addDoc(col(db, 'reports'), {
                        type: 'programme',
                        period: cohort?.currentPeriod || 'current',
                        programId: filterProgramId !== 'all' ? filterProgramId : null,
                        cohortId: filterCohortId !== 'all' ? filterCohortId : null,
                        clientId: filterClientId !== 'all' ? filterClientId : null,
                        scopeLabel: headerSubtitle || null,
                        content: editDraft,
                        savedBy: 'team',
                        createdAt: sts(),
                      });
                      setSavedReport(editDraft);
                      setReport(editDraft);
                      setEditing(false);
                    } catch(err) { console.error(err); }
                    finally { setSaving(false); }
                  }}>
                  {saving ? 'Saving…' : '✓ Save final version'}
                </button>
                <button className="gm-btn gm-btn-outline" onClick={() => setEditing(false)}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="gm-ai-summary-body">
              <ReportRenderer text={savedReport || report} />
            </div>
          )}

          {/* Company status table */}
          <div style={{ marginTop:32 }}>
            <h3 style={{ fontSize:'0.95rem', fontWeight:700, marginBottom:12 }}>Company status — {cohort?.currentPeriod}</h3>
            <table className="gm-table">
              <thead>
                <tr><th>Company</th><th>Current stage</th><th>Progress</th><th>Target</th><th>Status</th></tr>
              </thead>
              <tbody>
                {companies
                  .sort((a,b) => STAGES.indexOf(b.currentStage) - STAGES.indexOf(a.currentStage))
                  .map(c => {
                    const color = STAGE_COLORS[c.currentStage] || '#4a5481';
                    const onTrack = STAGES.indexOf(c.currentStage) >= STAGES.indexOf(c.targetStage) - 1;
                    return (
                      <tr key={c.id}>
                        <td style={{ fontWeight:600 }}>{c.name}</td>
                        <td><span style={{ color, fontWeight:600, fontSize:'0.82rem' }}>{c.currentStage}</span></td>
                        <td>
                          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                            <div className="gm-mini-progress" style={{ width:70 }}>
                              <div className="gm-mini-progress-fill" style={{ width:`${c.currentStageProgress||0}%`, background:color }} />
                            </div>
                            <span style={{ fontSize:'0.72rem', color:'#6b6b6b' }}>{c.currentStageUpgrade}</span>
                          </div>
                        </td>
                        <td style={{ fontSize:'0.78rem' }}>{c.targetStage}</td>
                        <td>
                          {onTrack
                            ? <span className="gm-validated-badge">On track</span>
                            : <span className="gm-pending-badge" style={{ background:'#fef2f2', color:'#dc2626', borderColor:'#fecaca' }}>Needs acceleration</span>}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Report renderer — parses markdown to styled HTML ─────────

// ─── Team programme selector ──────────────────────────────────
function TeamProgrammeSelector({ clients, programs, cohorts, filterClientId, setFilterClientId, filterProgramId, setFilterProgramId, filterCohortId, setFilterCohortId }) {
  const visiblePrograms = filterClientId === 'all' ? programs : programs.filter(p => p.clientId === filterClientId);
  const visibleCohorts = filterProgramId === 'all'
    ? (filterClientId === 'all' ? cohorts : cohorts.filter(c => visiblePrograms.find(p => p.id === c.programId)))
    : cohorts.filter(c => c.programId === filterProgramId);

  const selectedLabel = filterCohortId !== 'all'
    ? cohorts.find(c => c.id === filterCohortId)?.name
    : filterProgramId !== 'all'
    ? programs.find(p => p.id === filterProgramId)?.name
    : filterClientId !== 'all'
    ? clients.find(c => c.id === filterClientId)?.name
    : 'All programmes';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {/* Client selector */}
      <select value={filterClientId} onChange={e => setFilterClientId(e.target.value)}
        style={{ padding: '6px 10px', border: '1.5px solid var(--gm-primary-light)', borderRadius: 6, fontSize: '0.78rem', fontFamily: 'inherit', color: 'var(--gm-primary)', background: 'var(--gm-primary-faint)', cursor: 'pointer' }}>
        <option value="all">All clients</option>
        {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>

      {/* Program selector */}
      {filterClientId !== 'all' && visiblePrograms.length > 0 && (
        <select value={filterProgramId} onChange={e => setFilterProgramId(e.target.value)}
          style={{ padding: '6px 10px', border: '1.5px solid var(--gm-primary-light)', borderRadius: 6, fontSize: '0.78rem', fontFamily: 'inherit', color: 'var(--gm-primary)', background: 'var(--gm-primary-faint)', cursor: 'pointer' }}>
          <option value="all">All programs</option>
          {visiblePrograms.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      )}

      {/* Cohort selector */}
      {filterProgramId !== 'all' && visibleCohorts.length > 0 && (
        <select value={filterCohortId} onChange={e => setFilterCohortId(e.target.value)}
          style={{ padding: '6px 10px', border: '1.5px solid var(--gm-primary-light)', borderRadius: 6, fontSize: '0.78rem', fontFamily: 'inherit', color: 'var(--gm-primary)', background: 'var(--gm-primary-faint)', cursor: 'pointer' }}>
          <option value="all">All cohorts</option>
          {visibleCohorts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      )}

      {/* Active filter badge */}
      {(filterClientId !== 'all' || filterProgramId !== 'all' || filterCohortId !== 'all') && (
        <>
          <span style={{ fontSize: '0.72rem', color: 'var(--gm-primary)', fontWeight: 600, background: 'var(--gm-primary-faint)', border: '1px solid var(--gm-primary-light)', borderRadius: 20, padding: '3px 10px' }}>
            {selectedLabel}
          </span>
          <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '0.75rem', padding: '2px 6px' }}
            onClick={() => { setFilterClientId('all'); setFilterProgramId('all'); setFilterCohortId('all'); }}>
            ✕ Clear
          </button>
        </>
      )}
    </div>
  );
}
